import { isEqual } from "lodash";
import { useEffect, useRef, Dispatch, SetStateAction, useState } from "react";
import {
  isReadyMessage,
  isUserDrawnAreaMessage,
  isAnalysisResultMessage,
  formatAnalysisResults,
  isValidJson,
  MapMessage,
  FeatureCollection,
  isUserFileUploadedMessage,
  parseMapData,
} from "./mapMessages";
import { useAppSelector } from "@app/hooks";
import { selectCurrentProject, selectTemporaryMapData } from "@features/project/projectSlice";
import { Project } from "@typedefs/interfaces";
import { ClimateIdentifiers } from "./ClimateIdentifier";
import { sendMapConfigurationToExActMap } from "./mapConfigUtils";

interface UseMapCommunicationProps {
  messagesState: [string[], Dispatch<SetStateAction<string[]>>];
  onError?: (error: string) => void;
  identifiersInitialValues: ClimateIdentifiers;
}

interface UseMapCommunicationReturn {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  clearMap: () => void;
  error: string | null;
  identifiers: ClimateIdentifiers;
  areaToSave: FeatureCollection | null;
  setAreaToSave: Dispatch<SetStateAction<FeatureCollection | null>>;
}

export const useMapCommunication = ({
  messagesState,
  onError,
  identifiersInitialValues
}: UseMapCommunicationProps): UseMapCommunicationReturn => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [messages, setMessages] = messagesState;
  const [hasSentConfiguration, setHasSentConfiguration] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [identifiers, setIdentifiers] = useState<ClimateIdentifiers>(identifiersInitialValues);
  const [areaToSave, setAreaToSave] = useState<FeatureCollection | null>(null);
  const [previousProjectId, setPreviousProjectId] = useState<number | null>(null);
  const temporaryMapData = useAppSelector(selectTemporaryMapData);

  const { project } = useAppSelector(selectCurrentProject);

  // Function to handle errors consistently
  const handleError = (errorMsg: string) => {
    setError(errorMsg);
    onError?.(errorMsg);
    console.error("Error", errorMsg);
  };

  // Function to send configuration data to the iframe
  const sendConfigurationToMap = async (project?: Project) => {
    if (!iframeRef.current?.contentWindow) {
      handleError("Iframe not initialized");
      return;
    }

    let shape: FeatureCollection | null = null;
    if (project?.map_data) {
      const parsedMapData = parseMapData(project.map_data);
      // Only use map_data if it's a FeatureCollection (Exact tool)
      // Ignore if it's a URL (B-intact tool)
      if (parsedMapData.type === 'feature-collection') {
        shape = parsedMapData.data;
      }
    } else if (temporaryMapData) {
      shape = temporaryMapData;
    }

    try {
      await sendMapConfigurationToExActMap(iframeRef.current, shape, project);
      setMessages(prev => [...prev, "Configuration sent to map"]);
      setHasSentConfiguration(true);
      setError(null);
    } catch (err) {
      handleError(`Failed to send configuration: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // Function to clear the map and reset state
  const clearMap = () => {
    try {
      setMessages([]);
      setHasSentConfiguration(false);
      setError(null);
      if (iframeRef.current) {
        // Reassigning src to itself forces the iframe to reload — intentional browser trick.
        // biome-ignore lint/correctness/noSelfAssign: intentional iframe reload
        iframeRef.current.src = iframeRef.current.src;
      }
    } catch (err) {
      handleError(`Failed to clear map: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // Function to handle analysis results
  const handleAnalysisResult = (messageData: any) => {
    const formattedResults = formatAnalysisResults(messageData);
    setIdentifiers(formattedResults);
  };

  // Function to process map messages
  const processMapMessage = (message: string) => {
    if (message === 'ready') return;
    if (!isValidJson(message)) return;

    let projectArea: FeatureCollection | null = null;
    if (project?.map_data) {
      const parsedMapData = parseMapData(project.map_data);
      if (parsedMapData.type === 'feature-collection') {
        projectArea = parsedMapData.data;
      }
    }
    
    const messageData: MapMessage = JSON.parse(message);

    if ((isUserDrawnAreaMessage(messageData) || isUserFileUploadedMessage(messageData)) && !isEqual(projectArea, messageData.message)) {
      setAreaToSave(messageData.message);
    } else if (isAnalysisResultMessage(messageData)) {
      handleAnalysisResult(messageData);
    }
  };

  // Handle messages from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        // Ignore react devtools messages
        if (event.data?.source && /^react-devtools/gi.test(event.data.source)) {
          return;
        }

        // Handle error messages from iframe
        if (event.data === 'error') {
          handleError('Error received from map');
          return;
        }

        setMessages(prev => [...prev, JSON.stringify(event.data, null, 2)]);
        setError(null);
      } catch (err) {
        handleError(`Failed to process message: ${err instanceof Error ? err.message : String(err)}`);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [setMessages]);

  // Process latest message
  useEffect(() => {
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      processMapMessage(latestMessage);
    } else {
      setIdentifiers(identifiersInitialValues);
    }
  }, [messages, identifiersInitialValues, project?.map_data]);

  // Send configuration to map when it's ready
  useEffect(() => {
    if (iframeRef.current) {
      try {
        const hasReceivedReadyMessage = messages.some(msg => isReadyMessage(msg));
        if (hasReceivedReadyMessage && !hasSentConfiguration)
          sendConfigurationToMap(project ?? undefined);
      } catch (err) {
        handleError(`Failed to process ready message: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  }, [messages, hasSentConfiguration, project, temporaryMapData]);

  // Clear map when project ID changes
  useEffect(() => {
    if (project && project.id !== previousProjectId) clearMap();
    if (project) setPreviousProjectId(project.id);
  }, [project?.id]);

  return {
    iframeRef,
    clearMap,
    error,
    identifiers,
    areaToSave,
    setAreaToSave
  };
};